<template>
  <div class="mb-10 flex size-full flex-col items-center md:max-w-7xl md:px-5 lg:px-10">
    <div class="mb-3 flex w-full items-center px-2 text-gray-700 md:px-0">
      <p class="flex flex-col self-end text-xl md:text-2xl lg:text-3xl">
        <span>Cotizaciones</span>
        <a
          :href="`/makers/${maker.slug}`"
          class="text-sm font-light md:text-base"
          target="_blank"
        >
          Ver perfil público
        </a>
      </p>
      <button
        v-if="!showNewPreOrderCreation"
        class="ml-auto flex items-center gap-2 rounded-md border border-gray-700 px-3 py-2 hover:bg-gray-700 hover:text-white"
        @click="createPreOrder"
      >
        <PlusIcon class="size-5" />
        <span> Crear cotización </span>
      </button>
    </div>
    <div class="flex size-full flex-col items-center rounded-md bg-white px-2 py-3 md:px-4 md:py-5 lg:px-10">
      <lokal-loading
        v-if="preOrderIsLoading"
        :loading="preOrderIsLoading"
      ></lokal-loading>
      <template v-else>
        <maker-pre-order-list-manage
          v-if="showNewPreOrderCreation"
          :maker="maker"
          :pre-order="selectedPreOrder"
          @close="(showNewPreOrderCreation = false)"
        />
        <template
          v-for="preOrder in preOrders"
          v-else
          :key="preOrder.id"
        >
          <maker-pre-order-list-card
            :pre-order="preOrder"
            @set-pre-order="setSelectedPreOrder"
          />
        </template>
      </template>
      <pagination-component
        v-if="!preOrderIsLoading && preOrders.length > 0 && !showNewPreOrderCreation"
        class="mt-auto"
        :total-pages="totalPages"
        :current-page="currentPage"
        @update:current-page="updateCurrentPage"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useQuery } from 'vue-query';
import { PlusIcon } from '@heroicons/vue/24/outline';
import preOrdersApi from 'api/pre-orders';
import useUrlParamStore from 'stores/url-param-store';
import PaginationComponent from './shared/pagination-component.vue';
import useCustomParseInt from './use/custom-parse-int';
import useUrlMethods from './use/url-methods';
import MakerPreOrderListCard from './maker-pre-order-list-card.vue';
import MakerPreOrderListManage from './maker-pre-order-list-manage.vue';

interface MakersProductListProps {
  maker: Maker;
  withMakerImage?: boolean;
}

const props = withDefaults(defineProps<MakersProductListProps>(), {
  withMakerImage: true,
});

const currentPage = ref(1);
const showNewPreOrderCreation = ref(false);
const selectedPreOrder = ref({} as PreOrder);

function createPreOrder() {
  selectedPreOrder.value = {} as PreOrder;
  showNewPreOrderCreation.value = true;
}

function setSelectedPreOrder(preOrder: PreOrder) {
  if (preOrder.status === 'draft') {
    selectedPreOrder.value = preOrder;
    showNewPreOrderCreation.value = true;
  }
}

const queryParams = computed(() => {
  const params = 'q[s]=created_at+desc';

  return new URLSearchParams(params).toString();
});
const preOrderQuery = useQuery(
  ['pre-orders', props.maker.id, currentPage, queryParams],
  () => preOrdersApi.index(props.maker.id, currentPage.value, queryParams.value),
  { keepPreviousData: true },
);
const preOrderIsLoading = computed(() => preOrderQuery.isLoading.value || preOrderQuery.isPreviousData.value);
const preOrders = computed(() => preOrderQuery.data.value?.data.preOrders as PreOrder[]);
const totalResources = computed(() => useCustomParseInt(preOrderQuery.data.value?.headers['x-total']));
const resourcesPerPage = computed(() => useCustomParseInt(preOrderQuery.data.value?.headers['x-per-page']));
const totalPages = computed(() => Math.ceil(totalResources.value / resourcesPerPage.value));

const { updateUrlParams, getQueryParam } = useUrlMethods();
function updateQueryParams() {
  updateUrlParams({ page: `${currentPage.value}` });
}

function updateCurrentPage(page: number) {
  currentPage.value = page;
}

function setPage() {
  const page = getQueryParam('page');

  if (page) {
    currentPage.value = parseInt(page, 10);
  }
}

const urlParamStore = useUrlParamStore();
watch(currentPage, updateQueryParams);
onMounted(setPage);
onUnmounted(() => {
  urlParamStore.paramsToUpdate.page = '';
});
</script>
